import {
  UNESTIMATED_ASSIGNMENT_MINUTES,
  isPayableStage,
  type CapacityCheckInput,
  type PayableStage,
  type WorkflowStage,
} from '@tailoring-crm/shared';

/**
 * Turning what a screen collected into the request the contract carries.
 *
 * THE ONLY ARITHMETIC IN THE CAPACITY FEATURE LIVES HERE, and it is unit conversion and nothing
 * else. No date is computed, no day is added to, no load is summed and no feasibility is judged
 * on this side of the wire — the engine owns all of that, and every number the screens render is
 * read straight off its answer.
 */

/** Hours as somebody typed them, as the whole minutes the contract carries. Blank is nothing. */
export function minutesFromHours(hours: string): number {
  const trimmed = hours.trim();
  if (trimmed === '') return 0;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * 60);
}

/**
 * The stage a garment being booked is proposed against.
 *
 * Stitching, because that is the stage every garment passes through and the one a shop is short
 * of when it cannot promise a date. A shop that has switched stitching off gets its first enabled
 * payable stage instead, and a shop with no payable stage at all gets nothing — there is no work
 * type to ask about, so there is no question to ask. The key is named HERE, in a module, rather
 * than in a component: no screen in this feature knows a stage key exists.
 */
export function bookingTaskType(enabledStages: readonly WorkflowStage[]): PayableStage | undefined {
  const payable = enabledStages.filter(isPayableStage);
  return payable.find((stage) => stage === 'stitching') ?? payable[0];
}

export interface BookingCapacityInput {
  /** The order-level promised date, exactly as the input holds it. Blank means no question. */
  deliveryDay: string;
  /** Garment lines on the draft that have a garment chosen. */
  garmentLineCount: number;
  taskType: PayableStage | undefined;
}

/**
 * The booking draft as a capacity question, or `null` when there is no question to ask.
 *
 * ONE PROPOSED TASK PER GARMENT LINE. The estimate is the contract's own published figure for
 * work carrying none — `UNESTIMATED_ASSIGNMENT_MINUTES`, the same number the engine charges an
 * open assignment nobody typed a number against, and the same number it echoes back on
 * `assumptions.unestimatedAssignmentMinutes`. A booking draft has no estimate field to read: a
 * shop books a blouse, it does not book four hours. The alternative the contract leaves is no
 * proposed tasks at all, and the shared request schema refuses that outright (`tasks` is
 * `min(1)`), so every promised date typed at a counter would be a 400 in the API's log and the
 * warning could never appear for anybody. Charging each garment the engine's own modest fallback
 * is not a guess this module invented; it is the one the contract already publishes.
 *
 * `priority`, `splittable` and `otMinutes` are left to the schema's defaults. This is an ADVISORY
 * check and the counter has said nothing about any of them.
 */
export function bookingCapacityRequest(input: BookingCapacityInput): CapacityCheckInput | null {
  const { deliveryDay, garmentLineCount, taskType } = input;
  if (deliveryDay === '' || garmentLineCount <= 0 || taskType === undefined) return null;

  return {
    deliveryDay,
    tasks: Array.from({ length: garmentLineCount }, () => ({
      taskType,
      estimatedMinutes: UNESTIMATED_ASSIGNMENT_MINUTES,
    })),
  };
}
